import dayjs from 'dayjs';
import { notification } from 'antd';
import { useTranslation } from 'react-i18next';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
    FC,
    ReactNode,
    useEffect,
    useState,
} from 'react';
import { IPagination, SearchParamValue } from '../../../interfaces/paginate.interface';
import { IRequest } from '../../../interfaces/requests.interface';
import { requestListeners } from '../../../sockets/modules/requests-socket.module';
import { RequestsContext } from '../context/requests.ctx';
import { getRequests } from '../../../api/requests.api';
import { useWorkspaceStore } from '../../../store/workspace.store';
import { ISocketEvent } from '../../../sockets/socket.interface';
import { mapperOrderToRequest } from '../utils/requests.util';
import { SOCKET_ACTIONS } from '../../../sockets/socket.constants';
import { useAuthStore } from '../../../store/auth.store';
import { IAssignPayload } from '../../../interfaces/booking.interface';
import { manualAssignDriver } from '../../../api/bookings.api';

const RequestsProvider: FC<{ children: ReactNode; }> = ({ children }) => {
    const { t } = useTranslation('small-fleet-orders');

    const PAGE = 1;
    const LIMIT = 10;

    const workspace = useWorkspaceStore((state) => state.data);
    const user = useAuthStore((state) => state.data);

    const [data, setData] = useState<IRequest[]>([]);
    const [total, setTotal] = useState<number>(0);
    const [loading, setLoading] = useState<boolean>(false);
    const [errorMessage, setErrorMessage] = useState<string | null>(null);
    const [assignDriver, setAssignDriver] = useState<IRequest | null>(null);
    const [viewSearch, setViewSearch] = useState<IRequest | null>(null);
    const [pagination, setPagination] = useState<IPagination>({
        limit: LIMIT,
        page: PAGE,
        total: 0,
        totalPages: 0,
    });

    const [searchParams, setSearchParams] = useSearchParams({
        page: PAGE.toString(),
        limit: LIMIT.toString(),
        startDate: dayjs().startOf('day').toISOString(),
        endDate: dayjs().endOf('day').toISOString(),
    });

    const navigate = useNavigate();

    const getParams = (): Record<string, any> => {
        const paramsObject: Record<string, any> = Object.fromEntries(searchParams.entries());
        if (paramsObject.status) {
            try {
                paramsObject.status = JSON.parse(paramsObject.status);
            } catch (error) {
                delete paramsObject.status;
            }
        }
        return paramsObject;
    };

    const fetchData = () => {
        setLoading(true);

        const query: any = {
            page: pagination.page,
            limit: pagination.limit,
            providerName: workspace?.keyName,
            sort: 'desc',
            ...getParams(),
        };

        getRequests<{ total: number; data: IRequest[] }>(query)
            .then((response) => {
                setData(response.data);
                setTotal(response.total);
            })
            .catch((error) => {
                const description = error?.response?.data?.message || error.message || 'An error occurred while fetching requests.';
                setErrorMessage(description);
            })
            .finally(() => setLoading(false));
    };

    const handleSearchParam = (key: string, value: SearchParamValue) => {
        if (value === null || value === undefined || (Array.isArray(value) && value.length === 0) || value === '') {
            searchParams.delete(key);
        } else if (Array.isArray(value)) {
            searchParams.set(key, JSON.stringify(value));
        } else if (value instanceof Date) {
            searchParams.set(key, value.toISOString());
        } else {
            searchParams.set(key, value.toString());
        }

        searchParams.set('page', '1');

        setPagination({
            ...pagination,
            page: 1,
        });

        setSearchParams(searchParams);
    };

    const onPageChange = (page: number, limit?: number) => {
        const nextLimit = limit ?? pagination.limit;
        const nextPage = limit && limit !== pagination.limit ? 1 : page;

        searchParams.set('page', nextPage.toString());
        searchParams.set('limit', nextLimit.toString());

        setPagination((prevPagination) => ({
            ...prevPagination,
            page: nextPage,
            limit: nextLimit,
        }));
        setSearchParams(searchParams);
    };

    const handleNewRequest = (newRequest: IRequest) => {
        setData((prevData) => {
            if (prevData.some((request) => request._id === newRequest._id)) {
                return prevData;
            }

            setTotal((prevTotal) => prevTotal + 1);
            setPagination((prevPagination) => ({
                ...prevPagination,
                total: prevPagination.total + 1,
                totalPages: Math.ceil((prevPagination.total + 1) / prevPagination.limit),
            }));

            return [newRequest, ...prevData];
        });
    };

    const handleUpdateRequest = (updatedRequest: IRequest) => {
        setData((prevData) => prevData.map(
            (request) => (request._id.toString() === updatedRequest._id.toString() ? { ...request, ...updatedRequest } : request),
        ));
    };

    const handleAssignDriver = (driverID: string, bookingID: string, orderID: string) => {
        const payload: IAssignPayload = {
            token: user?.token as string,
            driverID,
            bookingID,
            driverTag: workspace?.keyName as string,
            orderID,
        };

        return manualAssignDriver(payload)
            .then(() => {
                notification.success({
                    message: t('BOOKING_ASSIGNED_SUCCESSFULLY'),
                });
                navigate('/bookings/actives');
                return true;
            })
            .catch((error: any) => {
                const description = error?.error
                    ? t(error.error.toUpperCase())
                    : error?.message || t('UNKNOWN');
                notification.error({
                    message: 'Error',
                    description,
                });
                fetchData();
                return false;
            });
    };

    useEffect(() => {
        const unsubscribe = requestListeners.onUpdate((socketEvent: ISocketEvent) => {
            const { payload, action } = socketEvent;
            let request;
            switch (action) {
            case SOCKET_ACTIONS.NEW_ORDERS:
                request = mapperOrderToRequest(payload.data);
                handleNewRequest(request);
                break;
            case SOCKET_ACTIONS.UPDATE:
                request = mapperOrderToRequest(payload.data);
                handleUpdateRequest(request);
                break;
            default:
                break;
            }
        });
        return unsubscribe;
    }, []);

    return (
        <RequestsContext.Provider
            value={{
                data,
                total,
                pagination,
                assignDriver,
                setAssignDriver,
                loading,
                viewSearch,
                setViewSearch,
                errorMessage,
                fetchData,
                searchParams,
                handleSearchParam,
                onPageChange,
                handleAssignDriver,
            }}
        >
            {children}
        </RequestsContext.Provider>
    );
};

export { RequestsProvider };
